import { LOGOUT_RTK, RECEIVE_INITIAL_STATE } from "@/components/access/ducks/Actions"
import { RECEIVE_ROLES } from "@/components/permissions/admin/ducks/Actions"
import { RECEIVE_CURRENT_CONSTITUENT } from "@/components/profile/admin/ducks/Actions"
import { RECEIVE_UPDATE_USER } from "@/components/profile/ducks/Actions"
import filterDict from "@/components/search/admin/helpers/filterDict"
import {
  CLEAR_QUERY,
  CLEAR_SEARCH_SUGGESTIONS,
  CLEAR_SELECTED_USER,
  FETCH_MAP_LOCATIONS,
  FETCH_SELECTED_USER,
  FETCH_SUGGESTED_SEARCHES,
  FETCH_USER_SEARCH,
  FETCH_USER_SEARCH_NEXT_PAGE,
  HIDE_MAP_CTA,
  RECEIVE_INITIAL_SEARCH_ERROR,
  RECEIVE_MAP_LOCATIONS,
  RECEIVE_NEXT_PAGE_ERROR,
  RECEIVE_RECENT_SEARCHES,
  RECEIVE_SEARCH_TEXT_SUGGESTIONS,
  RECEIVE_SELECTED_USER,
  RECEIVE_SUGGESTED_SEARCHES,
  RECEIVE_USER_SEARCH_FINAL,
  RECEIVE_VOLUNTEER_ROLES,
  REFETCH_SEARCH,
  SET_SELECTED_USER,
  TURN_OFF_MAP_VIEW,
  TURN_ON_MAP_VIEW,
} from "@/components/search/ducks/Actions"
import handlePagination from "@/components/search/helpers/handlePagination"
import removeDupeQueries from "@/components/search/helpers/removeDupeQueries"

const cloneWithPrototype = (obj) => Object.assign(Object.create(Object.getPrototypeOf(obj)), obj)

/**
 * @typedef {Object} SearchUserState
 * @property {boolean} loadingSearch - Whether a search is currently loading
 * @property {boolean} loadingFilteredSearch - Whether a filtered search is currently loading
 * @property {boolean} loadingNextPage - Whether the next page is currently loading
 * @property {boolean} fetchingSelectedUser - Whether a selected user is being fetched
 * @property {Array|null} selectedUserSubscriptions - Selected user subscriptions
 * @property {Array} searchAutocompleteSuggestions - Search autocomplete suggestions
 * @property {Object} filters - Current search filters
 * @property {boolean} filtered - Whether search is filtered
 * @property {number} filterCount - Number of active filters
 * @property {Object} filterDict - Filter dictionary
 * @property {Array} rolesOptions - Available role options
 * @property {Array} volunteerRolesOptions - Available volunteer role filter options
 * @property {boolean} clearUserDrawer - Whether to clear user drawer
 * @property {string} query - Current search query
 * @property {boolean} selectedUserIsConstituent - Whether selected user is a constituent
 * @property {number|null} selectedUserId - Selected user ID
 * @property {import("@/types").User|null} selectedUser - Selected user object
 * @property {Array} locations - Search results locations
 * @property {boolean} loading - Whether locations are loading
 * @property {boolean} mapView - Whether map view is enabled
 * @property {boolean} hideMapCTA - Whether to hide map CTA
 * @property {boolean} loadingSuggestedSearchesNextPage - Whether suggested searches next page is loading
 * @property {Object} results - Search results
 * @property {Object} suggestedSearches - Suggested searches
 * @property {boolean} [initialSearchError] - Whether there was an initial search error
 * @property {string} [initialSearchErrorText] - Initial search error text
 * @property {boolean} [nextPageError] - Whether there was a next page error
 * @property {boolean} [forceReload] - Whether to force reload
 * @property {boolean} [fetchingFirstPage] - Whether first page is being fetched
 * @property {Object} [displayExplanation] - Search explanation display
 * @property {Array} [recentSearches] - Recent searches
 */

const clearLoading = {
  loadingSearch: false,
  loadingFilteredSearch: false,
  loadingNextPage: false,
  fetchingSelectedUser: false,
  selectedUserSubscriptions: null,
  searchAutocompleteSuggestions: [],
  filters: {},
  filtered: false,
  filterCount: 0,
  filterDict,
}

const defaultState = {
  ...clearLoading,
  rolesOptions: [],
  volunteerRolesOptions: [],
  clearUserDrawer: false,
  query: "",
  selectedUserIsConstituent: false,
  selectedUserId: null,
  selectedUser: null,
  selectedUserSubscriptions: null,
  locations: [],
  loading: false,
  mapView: false,
  hideMapCTA: false,
  loadingSuggestedSearchesNextPage: false,
  results: {},
  suggestedSearches: {},
}

const areFilterOptionsEqual = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return false
  }
  if (left.length !== right.length) {
    return false
  }

  return left.every((leftOption, index) => {
    const rightOption = right[index]
    return leftOption?.value === rightOption?.value && leftOption?.label === rightOption?.label
  })
}

/**
 * Search results reducer function
 * @param {SearchUserState} oldState - Previous state
 * @param {Object} action - Redux action
 * @returns {SearchUserState} New state
 */
const resultsReducer = (oldState = defaultState, action) => {
  Object.freeze(oldState)
  switch (action.type) {
    case LOGOUT_RTK.type:
      return defaultState

    case SET_SELECTED_USER:
      return {
        ...oldState,
        selectedUserId: action.userId,
        selectedUser: action.user,
        fetchingSelectedUser: false,
        clearUserDrawer: false,
        selectedUserIsConstituent: true,
      }
    case HIDE_MAP_CTA:
      return {
        ...oldState,
        hideMapCTA: true,
      }

    // RECEIVE_CURRENT_CONSTITUENT is a DATA UPDATE action: it refreshes the
    // constituent object after a profile mutation (e.g. volunteer role add/delete,
    // position edit, donation, etc.). It intentionally does NOT set
    // selectedUserIsConstituent — that is a SELECTION concern handled exclusively
    // by SET_SELECTED_USER and RECEIVE_SELECTED_USER. The selection events always
    // fire BEFORE any edit operations, so selectedUserIsConstituent is already
    // true in admin-flow when this handler runs. Setting it here would incorrectly
    // flip it to true during self-flow (user viewing their own profile), which
    // breaks useShowEdit() and hides all Edit buttons across the profile drawer.
    // See: apps/frontend/src/features/volunteer-roles/sync.ts
    case RECEIVE_CURRENT_CONSTITUENT:
      return {
        ...oldState,
        selectedUser: action.constituent,
      }

    case RECEIVE_INITIAL_STATE:
      return {
        ...oldState,
        recentSearches: action.response.recentSearches,
      }

    case RECEIVE_RECENT_SEARCHES:
      return {
        ...oldState,
        recentSearches: action.searches,
      }

    case FETCH_SELECTED_USER:
      return {
        ...oldState,
        fetchingSelectedUser: true,
      }

    case CLEAR_SELECTED_USER:
      return {
        ...oldState,
        selectedUserId: null,
        selectedUser: null,
        selectedUserSubscriptions: null,
        selectedUserIsConstituent: false,
        fetchingSelectedUser: false,
      }

    case RECEIVE_SELECTED_USER:
      return {
        ...oldState,
        selectedUser: action.user,
        selectedUserIsConstituent: true,
        fetchingSelectedUser: false,
      }

    case RECEIVE_INITIAL_SEARCH_ERROR: {
      const respJSON = action.response.responseJSON
      const description = respJSON?.description

      return {
        ...oldState,
        ...clearLoading,
        initialSearchError: true,
        initialSearchErrorText: description,
      }
    }

    case RECEIVE_NEXT_PAGE_ERROR:
      return {
        ...oldState,
        ...clearLoading,
        nextPageError: true,
      }

    case RECEIVE_ROLES: {
      const { roles } = action
      // console.log(
      //   `[REDUCER] RECEIVE_ROLES called with ${roles?.length || 0} roles`,
      // )

      // Only create new filterDict if there are actual roles to add
      // This prevents infinite re-render loops when roles array is empty
      if (!roles || roles.length === 0) {
        // console.log(`[REDUCER] No roles to add, returning existing filterDict`)
        return {
          ...oldState,
          rolesOptions: [],
        }
      }

      // FIX: Create a deep copy only when we have roles to add
      const newFilterDict = cloneWithPrototype(oldState.filterDict)
      let newRoles = []

      if (roles && newFilterDict) {
        roles.forEach((role) => {
          const newRole = {
            title: role[0],
            defaultValue: "",
            stateFieldName: `RoleID${role[4]}`,
            classYearField: `RoleID${role[4]}ClassYear`,
            options: [
              { value: "true", label: `Is ${role[0]}` },
              { value: "false", label: `Is Not ${role[0]}` },
            ],
          }

          console.log(`[REDUCER] Adding role to new filterDict: RoleID${role[4]}`)
          newFilterDict[`RoleID${role[4]}`] = newRole
          newFilterDict[`RoleID${role[4]}ClassYear`] = {
            title: "Class Year",
            defaultValue: 0,
            stateFieldName: `RoleID${role[4]}ClassYear`,
          }
          newRoles = newRoles.concat([newRole])
        })
      }

      // console.log(
      //   `[REDUCER] FIXED: returning new filterDict reference:`,
      //   newFilterDict !== oldState.filterDict,
      // )
      return {
        ...oldState,
        filterDict: newFilterDict,
        rolesOptions: newRoles,
      }
    }

    case RECEIVE_VOLUNTEER_ROLES: {
      const { volunteerRoles } = action
      const currentVolunteerRoleFilter = oldState.filterDict.volunteerRoleId || {}
      const currentOptions = currentVolunteerRoleFilter.options || []

      if (!volunteerRoles || volunteerRoles.length === 0) {
        const alreadyCleared =
          currentOptions.length === 0 && oldState.volunteerRolesOptions.length === 0

        if (alreadyCleared) {
          return oldState
        }

        const newFilterDict = cloneWithPrototype(oldState.filterDict)
        newFilterDict.volunteerRoleId = {
          ...currentVolunteerRoleFilter,
          options: [],
        }

        return {
          ...oldState,
          filterDict: newFilterDict,
          volunteerRolesOptions: [],
        }
      }

      const nextOptions = volunteerRoles.map((role) => ({
        value: String(role.id),
        label: role.role,
      }))

      if (areFilterOptionsEqual(currentOptions, nextOptions)) {
        return oldState
      }

      const newFilterDict = cloneWithPrototype(oldState.filterDict)
      newFilterDict.volunteerRoleId = {
        ...currentVolunteerRoleFilter,
        options: nextOptions,
      }

      return {
        ...oldState,
        filterDict: newFilterDict,
        volunteerRolesOptions: [newFilterDict.volunteerRoleId],
      }
    }

    case RECEIVE_USER_SEARCH_FINAL: {
      // Handle pagination
      if (oldState.query !== action.response.query) {
        return oldState
      }
      const { response, results } = action
      let countModifier = 0

      if (response.filters && "city" in response.filters) {
        countModifier = -4
      }
      const filterCount =
        (response.filters && Object.keys(response.filters).length + countModifier) || 0

      const newState = {
        ...oldState,
        ...clearLoading,
        initialSearchError: false,
        nextPageError: false,
        results,
        fetchingFirstPage: false,
        filters: { ...action.newFilters },
        filtered: response.filtered || false,
        filterCount,
        displayExplanation: action.newExplanation,
      }

      // newState.displayExplanation = updateExplanation(oldState, newState)

      return newState
    }

    case REFETCH_SEARCH:
      return {
        ...oldState,
        loadingFilteredSearch: oldState.filtered,
        forceReload: true,
        clearUserDrawer: false,
        nextPageError: false,
        initialSearchError: false,
        fetchingFirstPage: true,
      }

    case FETCH_USER_SEARCH_NEXT_PAGE:
      return {
        ...oldState,
        loadingNextPage: true,
        nextPageError: false,
        forceReload: false,
        initialSearchError: false,
      }

    case FETCH_USER_SEARCH:
      if (!action.fetching) {
        return {
          ...oldState,
        }
      }
      return {
        ...oldState,
        query: action.params.query,
        filters: action.params.filters,
        loadingSearch: true,
        loadingFilteredSearch: !!action.params.filters,
        nextPageError: false,
        forceReload: false,
        initialSearchError: false,
        fetchingFirstPage: true,
      }

    case RECEIVE_UPDATE_USER:
      return {
        ...oldState,
        forceReload: true,
      }

    case CLEAR_QUERY:
      return {
        ...oldState,
        query: null,
      }

    case CLEAR_SEARCH_SUGGESTIONS:
      return {
        ...oldState,
        searchAutocompleteSuggestions: [],
      }

    case RECEIVE_SEARCH_TEXT_SUGGESTIONS:
      return {
        ...oldState,
        searchAutocompleteSuggestions: action.suggestedSearches,
      }

    case FETCH_MAP_LOCATIONS:
      return {
        ...oldState,
        locations: [],
        loading: true,
      }

    case TURN_ON_MAP_VIEW:
      return {
        ...oldState,
        mapView: true,
      }

    case TURN_OFF_MAP_VIEW:
      return {
        ...oldState,
        mapView: false,
      }

    case RECEIVE_MAP_LOCATIONS:
      if (action.query && action.query.toString() !== oldState.query.toString()) {
        return oldState
      }
      return {
        ...oldState,
        locations: action.locations,
        loading: false,
      }

    case FETCH_SUGGESTED_SEARCHES:
      return {
        ...oldState,
        loadingSuggestedSearchesNextPage: true,
      }

    case RECEIVE_SUGGESTED_SEARCHES: {
      const results = handlePagination(action.suggestedSearches, oldState, "suggestedSearches")

      removeDupeQueries(results)

      if (results.pages.slice(-1)[0].length === 0) {
        results.nextPage = null
      }
      return {
        ...oldState,
        suggestedSearches: results,
        loadingSuggestedSearchesNextPage: false,
      }
    }

    default:
      return oldState
  }
}

export default resultsReducer
